import { getFeatureFlags } from "@/lib/feature-flags";
import type {
  SerializedViewState,
  SavedViewConfig,
  TableFilters,
} from "@/components/shared/UniversalCRMTable/types/shared";
import { normalizeToSerializedViewState } from "@/components/shared/UniversalCRMTable/types/shared";
import type { SavedView } from "@/lib/types/saved-views";

// Re-export types for backward compatibility
export type { SavedView, SavedViewVisibility } from "@/lib/types/saved-views";

/**
 * Convert SavedView (API type) to SavedViewConfig (UI type)
 * Handles the mapping between different filter representations
 */
export function savedViewToConfig(view: SavedView): SavedViewConfig {
  const normalizedFilters = normalizeToSerializedViewState(view.filters);

  return {
    id: view.id,
    name: view.name,
    resource: view.resource,
    filters: normalizedFilters.filters || {},
    sort: normalizedFilters.sort || { field: "", direction: "asc" as const },
    visibleColumns: normalizedFilters.visibleColumns || [],
    visibility: view.visibility,
    shared_with_user_ids: view.shared_with_user_ids,
    created_by: view.created_by,
    created_at: view.created_at,
    updated_at: view.updated_at,
  };
}

export function isFeatureEnabled() {
  const flags = getFeatureFlags();
  return Boolean(flags.savedViews);
}

export function isFavoritesEnabled() {
  const flags = getFeatureFlags();
  return Boolean(flags.savedViewFavorites) && isFeatureEnabled();
}

/**
 * Serialize a SerializedViewState to a JSON string for storage.
 * Supabase's GraphQL JSON type requires the filters to be passed as a JSON string.
 *
 * @param viewState - The SerializedViewState or flat filter object from the filter context
 * @returns A JSON string representation of the filters, safe for GraphQL mutations
 */
export function serializeFiltersForStorage(
  viewState: SerializedViewState | TableFilters
): string {
  return JSON.stringify(pickFiltersForStorage(viewState));
}

/**
 * The object half of {@link serializeFiltersForStorage}: keeps only the known
 * `SerializedViewState` keys (so page-local scratch state never reaches the
 * `filters` jsonb column), or passes a flat filter map through untouched.
 *
 * Callers that hand the result to `serializeGraphqlInput("saved_views", …)`
 * should use this — that helper does the `JSON.stringify` the JSON scalar needs.
 */
export function pickFiltersForStorage(
  viewState: SerializedViewState | TableFilters
): SerializedViewState | TableFilters {
  const result: SerializedViewState = {};

  const isSerializedViewState =
    viewState &&
    ("filters" in viewState ||
      "advancedFilters" in viewState ||
      "search" in viewState ||
      "sort" in viewState ||
      "visibleColumns" in viewState);

  // A flat filter map is stored flat, exactly as before — `normalizeToSerializedViewState`
  // re-wraps it on read. Changing the stored shape here would strand existing rows.
  if (!isSerializedViewState) {
    return { ...(viewState as TableFilters) };
  }

  const state = viewState as SerializedViewState;
  if (state.search) result.search = state.search;
  if (state.filters) result.filters = state.filters;
  if (state.advancedFilters) result.advancedFilters = state.advancedFilters;
  if (state.filterOperator) result.filterOperator = state.filterOperator;
  if (state.quickFilters) result.quickFilters = state.quickFilters;
  if (state.sort) result.sort = state.sort;
  if (state.visibleColumns) result.visibleColumns = state.visibleColumns;
  if (state.columnWidths) result.columnWidths = state.columnWidths;
  if (state.columnOrder) result.columnOrder = state.columnOrder;

  return result;
}
